import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutGrid,
  ClipboardList,
  Building2,
  FileText,
  Users,
  BarChart3,
  Menu,
  X,
} from "lucide-react";
import "../../../Styles/Admin/assessment/sidebar.css"; // src/pages/admin/assessment/Sidebar.js -> src/Styles/...

const Sidebar = () => {
  // mobile / tablet drawer (below 1024px) - on laptop/desktop nothing changes
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();

  // close the drawer whenever the route changes
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // every page opens at the very top - on first entry, on route change and
  // on browser refresh (stops the browser restoring the old scroll position)
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname]);

  // lock page scroll while the drawer is open, close it when resized to desktop
  useEffect(() => {
    document.body.style.overflow = mobileNavOpen ? "hidden" : "";
    const onResize = () => {
      if (window.innerWidth > 1023) setMobileNavOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      document.body.style.overflow = "";
    };
  }, [mobileNavOpen]);

  // close on Escape
  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMobileNavOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileNavOpen]);

  const closeNav = () => setMobileNavOpen(false);

  return (
    <>
      {/* Mobile / tablet top bar (hidden on laptop and desktop) */}
      <div className="ow-mobile-bar">
        <button
          type="button"
          className="ow-burger"
          aria-label="Open menu"
          aria-expanded={mobileNavOpen}
          onClick={() => setMobileNavOpen(true)}
        >
          <Menu size={22} />
        </button>
        <img
          src="/assets/lo.png"
          alt="Outstanding Workplaces"
          className="ow-mobile-logo"
        />
      </div>

      {mobileNavOpen && <div className="ow-overlay" onClick={closeNav}></div>}

      <aside className={`ow-sidebar ${mobileNavOpen ? "open" : ""}`}>
        <button
          type="button"
          className="ow-drawer-close"
          aria-label="Close menu"
          onClick={closeNav}
        >
          <X size={20} />
        </button>

        {/* Logo */}
        <div className="ow-sidebar-logo">
          <img
            src="/assets/lo.png"
            alt="Outstanding Workplaces"
            className="ow-logo-img"
          />
        </div>

        <div className="ow-sidebar-divider" />

        {/* Nav */}
        <nav className="ow-sidebar-nav">
          {/* No section label for Dashboard */}
          <ul className="ow-nav-list">
            <li>
              <NavLink
                to="/admin/assessment/dashboard"
                onClick={closeNav}
                className={({ isActive }) =>
                  `ow-nav-item ${isActive ? "active" : ""}`
                }
              >
                <LayoutGrid size={18} className="ow-nav-icon" />
                <span>Dashboard</span>
              </NavLink>
            </li>
          </ul>

          {/* ASSESSMENT */}
          <p className="ow-nav-section">Assessment</p>
          <ul className="ow-nav-list">
            <li>
              <NavLink
                to="/admin/assessment/quizzes"
                onClick={closeNav}
                className={({ isActive }) =>
                  `ow-nav-item ${isActive ? "active" : ""}`
                }
              >
                <ClipboardList size={18} className="ow-nav-icon" />
                <span>Quizzes</span>
              </NavLink>
            </li>
          </ul>

          {/* CLIENTS */}
          <p className="ow-nav-section">Clients</p>
          <ul className="ow-nav-list">
            <li>
              <NavLink
                to="/admin/assessment/add-companies"
                onClick={closeNav}
                className={({ isActive }) =>
                  `ow-nav-item ${isActive ? "active" : ""}`
                }
              >
                <Building2 size={18} className="ow-nav-icon" />
                <span>Companies</span>
              </NavLink>
            </li>
          </ul>

          {/* REPORTS */}
          <p className="ow-nav-section">Reports</p>
          <ul className="ow-nav-list">
            <li>
              <NavLink
                to="/admin/assessment/company-report"
                onClick={closeNav}
                className={({ isActive }) =>
                  `ow-nav-item ${isActive ? "active" : ""}`
                }
              >
                <FileText size={18} className="ow-nav-icon" />
                <span>Company Report</span>
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/admin/assessment/employees"
                onClick={closeNav}
                className={({ isActive }) =>
                  `ow-nav-item ${isActive ? "active" : ""}`
                }
              >
                <Users size={18} className="ow-nav-icon" />
                <span>Employee Report</span>
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/admin/assessment/report"
                onClick={closeNav}
                className={({ isActive }) =>
                  `ow-nav-item ${isActive ? "active" : ""}`
                }
              >
                <BarChart3 size={18} className="ow-nav-icon" />
                <span>Analytics</span>
              </NavLink>
            </li>
          </ul>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;